import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import releaseGroupSelectors from '../../../../redux/project_grouping/selectors';
import type { ProjectType } from '../../../../../typings/enums';
import type { UseInventoryFeaturesResult } from '../types';

import { useEnhancedScanFeaturesAvailable } from './useEnhancedScanFeaturesAvailable';

/**
 * A hook for figuring out which features of the inventory dropdown should be shown for a release.
 */
export function useInventoryFeaturesForReleaseGroup(): UseInventoryFeaturesResult {
  //  Note: that this part relies on the Release Groups Redux state
  const release = useSelector(releaseGroupSelectors.getRelease);

  const projectTypeSet = useMemo(() => {
    if (release) {
      return new Set<ProjectType>(release.projects.map(p => p.project.type));
    }

    return new Set<ProjectType>();
  }, [release]);

  const {
    areVendoredDependenciesAvailable,
    isBinaryAnalysisAvailable,
    isInventoryUIFeatureEnabled,
  } = useEnhancedScanFeaturesAvailable(projectTypeSet);

  // Snippets are not supported on the release group scope
  const areAnyGatedFeaturesShown = (
    areVendoredDependenciesAvailable ||
    isBinaryAnalysisAvailable
  );
  const shouldShowInventory = isInventoryUIFeatureEnabled && areAnyGatedFeaturesShown;

  return useMemo(() => ({
    shouldShowInventory,
    shouldShowSnippets: false, // snippets are not supported for release groups
    shouldShowBinaryAnalysis: isBinaryAnalysisAvailable,
    shouldShowVendored: areVendoredDependenciesAvailable,
    isInventoryUIFeatureEnabled,
    isLoading: !release,
  }), [
    shouldShowInventory,
    areVendoredDependenciesAvailable,
    isBinaryAnalysisAvailable,
    isInventoryUIFeatureEnabled,
    release,
  ]);
}
